import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { api } from "@/lib/api";

import HeroSection, { HERO_IMG } from "@/components/home/HeroSection";
import ServicesBento from "@/components/home/ServicesBento";
import ProcessSection from "@/components/home/ProcessSection";
import TestimonialsSection from "@/components/home/TestimonialsSection";

const LIVING_IMG = "https://images.pexels.com/photos/5942742/pexels-photo-5942742.jpeg?auto=compress&cs=tinysrgb&w=1200";

const STATS = [
  { id: "homes", label: "Homes serviced", value: "12,400+" },
  { id: "rating", label: "Avg. customer rating", value: "4.9 ★" },
  { id: "areas", label: "Service areas", value: "9 cities" },
  { id: "years", label: "Years in business", value: "11" },
];

export default function Home() {
  useSeo({
    title: "Full Indoor Air Services | Air Duct Cleaning · MD · DC · VA",
    description: "Premium air duct cleaning, dryer vent cleaning, and indoor air quality services across Maryland, Washington DC, and Virginia. Transparent pricing. NADCA-aligned process.",
    canonical: "https://capitalindoorair.com/",
    ogImage: HERO_IMG,
  });

  const [packages, setPackages] = useState([]);
  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data.packages || [])).catch(() => {});
  }, []);

  return (
    <div className="bg-bone" data-testid="home-page">
      <HeroSection />

      {/* Stats */}
      <section className="border-y border-black/5 bg-white/70" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.id} className="text-center md:text-left">
              <div className="font-heading text-3xl md:text-4xl text-ink tracking-tight">{s.value}</div>
              <div className="mt-1 eyebrow">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <ServicesBento />

      {/* Packages preview */}
      <section className="section-pad bg-surfaceAlt" data-testid="packages-preview">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
            <div>
              <div className="eyebrow mb-3">Service Packages</div>
              <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-medium">Transparent pricing.</h2>
            </div>
            <Link to="/packages" className="text-sm text-forest hover:underline" data-testid="see-all-packages">
              View all packages →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative card-surface p-8 hover-lift ${pkg.featured ? "ring-2 ring-clay/60" : ""}`}
                data-testid={`package-card-${pkg.id}`}
              >
                {pkg.featured && (
                  <span className="absolute -top-3 left-8 bg-clay text-white text-[10px] tracking-[0.22em] uppercase px-3 py-1 rounded-full">
                    Most popular
                  </span>
                )}
                <h3 className="font-heading text-2xl tracking-tight">{pkg.name}</h3>
                <p className="text-sm text-muted2 mt-2">{pkg.tagline}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-xs text-muted2">from</span>
                  <span className="font-heading text-4xl text-ink">${pkg.price_from}</span>
                </div>
                <ul className="mt-6 space-y-2">
                  {pkg.highlights.slice(0, 4).map((h) => (
                    <li key={h} className="flex gap-2 text-sm text-ink/80">
                      <CheckCircle2 size={16} className="text-forest mt-0.5 shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/book?package=${pkg.id}`}
                  className={`mt-7 inline-flex items-center gap-2 ${pkg.featured ? "btn-primary" : "btn-ghost"} w-full justify-center`}
                  data-testid={`select-package-${pkg.id}`}
                >
                  Choose {pkg.name} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProcessSection />
      <TestimonialsSection />

      {/* Final CTA */}
      <section className="section-pad" data-testid="final-cta">
        <div className="max-w-6xl mx-auto card-surface p-10 md:p-16 relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="eyebrow mb-3">Ready when you are</div>
              <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-medium">
                Cleaner air is one booking away.
              </h2>
              <p className="mt-5 text-muted2 leading-relaxed">
                Pick your package, choose a date and time, and we'll handle the rest. Same-week
                appointments available in most MD, DC, and VA cities.
              </p>
              <div className="mt-7 flex flex-wrap gap-4">
                <Link to="/book" className="btn-primary" data-testid="cta-book">Book online</Link>
                <Link to="/contact" className="btn-ghost" data-testid="cta-contact">Request a quote</Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img src={LIVING_IMG} alt="Cozy modern home" className="w-full h-[300px] md:h-[360px] object-cover" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
